import type { ViewershipDetail } from '../../../watchApi/types';

export type DemographicsBracket = {
  label: string;
  key: string;
  value?: number;
  partOfTotal?: boolean;
};

export type Demographics = {
  gender: DemographicsBracket[];
  age: DemographicsBracket[];
  ethnicity: DemographicsBracket[];
  income: DemographicsBracket[];
};

export const demographicsDefinitions = {
  gender: [
    { key: 'male', label: 'Male', partOfTotal: true },
    { key: 'female', label: 'Female', partOfTotal: true },
  ],
  age: [
    { key: 'age_1_17', label: '1 - 17', partOfTotal: true },
    { key: 'age_18_24', label: '18 - 24', partOfTotal: true },
    { key: 'age_25_34', label: '25 - 34', partOfTotal: true },
    { key: 'age_35_44', label: '35 - 44', partOfTotal: true },
    { key: 'age_45_54', label: '45 - 54', partOfTotal: true },
    { key: 'age_55_65', label: '55 - 65', partOfTotal: true },
    { key: 'age_65_plus', label: '65+', partOfTotal: true },
    { key: 'age_21_plus', label: '21+', partOfTotal: false },
  ],
  ethnicity: [
    { key: 'ethnicity_white', label: 'White or Caucasian', partOfTotal: true },
    {
      key: 'ethnicity_hispanic',
      label: 'Hispanic or Latino',
      partOfTotal: true,
    },
    {
      key: 'ethnicity_black',
      label: 'Black or African-American',
      partOfTotal: true,
    },
    {
      key: 'ethnicity_asian',
      label: 'Asian or Asian-American',
      partOfTotal: true,
    },
    { key: 'ethnicity_other', label: 'Other', partOfTotal: true },
  ],
  income: [
    { key: 'income_under_20', label: 'Under 20k', partOfTotal: true },
    { key: 'income_under_50', label: 'Under 50k', partOfTotal: false },
    { key: 'income_20_49', label: '20 - 49k', partOfTotal: true },
    { key: 'income_50_74', label: '50 - 74k', partOfTotal: true },
    { key: 'income_75_99', label: '75 - 99k', partOfTotal: true },
    { key: 'income_100_149', label: '100 - 149k', partOfTotal: false },
    { key: 'income_150_199', label: '150 - 199k', partOfTotal: false },
    { key: 'income_100_plus', label: '100k+', partOfTotal: true },
    { key: 'income_200_plus', label: '200k+', partOfTotal: false },
  ],
};

/**
 * Calculates brackets for a specific demographic.
 * @param viewership
 * @param definitions
 * @returns Brackets from the definitions, with calculated values as percentages
 */
const createDemographicsBrackets = (
  viewershipDetail: ViewershipDetail,
  definitions: DemographicsBracket[],
): DemographicsBracket[] => {
  const brackets: DemographicsBracket[] = [];
  const total = definitions.reduce((sum, definition) => {
    if (definition.partOfTotal) {
      const value =
        viewershipDetail?.[definition.key as keyof ViewershipDetail];
      return sum + (typeof value === 'number' ? value : 0);
    }
    return sum;
  }, 0);

  definitions.forEach((definition) => {
    brackets.push({
      label: definition.label,
      key: definition.key,
      value:
        (100 *
          (viewershipDetail &&
          typeof viewershipDetail[definition.key as keyof ViewershipDetail] ===
            'number'
            ? (viewershipDetail[
                definition.key as keyof ViewershipDetail
              ] as number)
            : 0)) /
        total,
    });
  });

  return brackets;
};

/**
 * Converts all the demographic brackets from actual values to percentages.
 * @param viewership
 * @returns viewership
 */
export const processDemographics = (
  viewershipDetail: ViewershipDetail,
): Demographics => {
  const demographics: Demographics = {
    gender: [],
    age: [],
    ethnicity: [],
    income: [],
  };

  demographics.gender = createDemographicsBrackets(
    viewershipDetail,
    demographicsDefinitions.gender,
  );
  demographics.age = createDemographicsBrackets(
    viewershipDetail,
    demographicsDefinitions.age,
  );
  demographics.ethnicity = createDemographicsBrackets(
    viewershipDetail,
    demographicsDefinitions.ethnicity,
  );
  demographics.income = createDemographicsBrackets(
    viewershipDetail,
    demographicsDefinitions.income,
  );

  return demographics;
};
